import { memo } from "react";
import { MiButton, MiStackedTypography } from "@miplatform/components";
import { BUTTON_VARIANTS, PERMISSIONS } from "@miplatform/constants";
import { formatDate } from "@miplatform/utils";
import { mdiThumbUpOutline } from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box } from "@mui/material";
import { ORDERS_DEFAULT_FIELD_WIDTH } from "../../ordersConstants";
import { OrdersListActionMenu } from "../ActionMenu/OrdersListActionMenu";
import useConfirmedOrderActions from "./useConfirmedOrderActions";

/**
 * ConfirmedOrderActions Component
 *
 * A React functional component that displays the actions available for a confirmed order.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.id - The unique identifier for the order.
 * @param {string} props.approvedStartDate - The approved start date for the order.
 *
 * @returns {JSX.Element} The rendered ConfirmedOrderActions component.
 */
export const ConfirmedOrderActions = memo((props) => {
  const { id, approvedStartDate, modifiedOn, onRefresh } = props;

  const { hasPermission } = usePermissions();

  const { isLoading, assignOrder } = useConfirmedOrderActions({
    id,
    modifiedOn,
    onRefresh,
  });

  const canAssignOrder = hasPermission(PERMISSIONS.ORDER.ASSIGN);

  return (
    <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
      <MiStackedTypography
        title="Approved Date"
        titleProps={{ width: ORDERS_DEFAULT_FIELD_WIDTH }}
        description={
          approvedStartDate ? formatDate(approvedStartDate, "MM/DD/YYYY") : "--"
        }
        descriptionProps={{ width: ORDERS_DEFAULT_FIELD_WIDTH }}
      />
      {canAssignOrder && (
        <MiButton
          title="Assign"
          variant={BUTTON_VARIANTS.OUTLINED}
          disabled={isLoading}
          startIcon={{
            path: mdiThumbUpOutline,
            iconSize: 0.8,
          }}
          onClick={assignOrder}
        />
      )}
      <OrdersListActionMenu
        menuViewConfig={{
          Reschedule: true,
          Hold: true,
          Cancel: true,
          OrderActivityDetails: true,
          ForceComplete: true,
          UndoConfirmation: true,
        }}
        {...props}
      />
    </Box>
  );
});
